import { motion } from "framer-motion";

/**
 * MultiSegmentGauge — Premium SVG donut chart showing Existing and Planned segments.
 */
const CircularGauge = ({
    existingPercentage = 0,
    plannedPercentage = 0,
    label = "USED",
    size = 130,
    existingColor = "#6366f1", // Indigo
    plannedColor = "#10b981",  // Emerald
    trackColor = "#f1f5f9",
    textColor = "text-slate-800",
}) => {
    const totalPercentage = existingPercentage + plannedPercentage;
    const clampedTotal = Math.min(100, Math.max(0, totalPercentage));
    
    const radius = (size / 2) - 12;
    const circumference = 2 * Math.PI * radius;
    
    // Calculate stroke offsets
    const existingOffset = circumference - (Math.min(100, existingPercentage) / 100) * circumference;
    const plannedOffset = circumference - (Math.min(100, plannedPercentage) / 100) * circumference;

    // Rotation for planned segment to start where existing ends
    // Rotation in degrees = (existingPercentage / 100) * 360
    const plannedRotation = (existingPercentage / 100) * 360;

    return (
        <div className="flex flex-col items-center justify-center relative">
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="filter transition-all duration-700"
            >
                {/* Subtle outer glow filter */}
                <defs>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Background track */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={trackColor}
                    strokeWidth={12}
                />

                {/* EXISTING Segment */}
                <motion.circle
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset: existingOffset }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={existingColor}
                    strokeWidth={12}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    filter="url(#glow)"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />

                {/* PLANNED Segment */}
                {plannedPercentage > 0 && (
                    <motion.circle
                        initial={{ strokeDashoffset: circumference }}
                        animate={{ strokeDashoffset: plannedOffset }}
                        transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={plannedColor}
                        strokeWidth={12}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        filter="url(#glow)"
                        // Stacked rotation: -90 (start) + existingPct rotation
                        transform={`rotate(${-90 + plannedRotation} ${size / 2} ${size / 2})`}
                    />
                )}
            </svg>

            {/* Inner Content — Overlayed for better centering and font control */}
            <div className="absolute inset-0 flex flex-col items-center justify-center mt-[-4px]">
                <motion.span 
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className={`text-2xl font-black tracking-tight ${textColor}`}
                >
                    {clampedTotal.toFixed(1)}<span className="text-[10px] ml-0.5 opacity-40">%</span>
                </motion.span>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-[-2px]">
                    {label}
                </span>
            </div>
        </div>
    );
};

export default CircularGauge;
